import { ChartTypeRegistry, TooltipItem } from 'chart.js'
import { getTypeObject } from 'helpers/global.helper/global.helper'
import i18n from 'i18next'
import moment from 'moment'
import { colors } from 'styles/variables'
import Datetime from 'utils/datetime/datetime'
import arrowDown from '/img/favicon/arrow-down.svg'
import arrowUp from '/img/favicon/arrow-up.svg'
import { ChartJSDatasetConfig } from '../Chart.types'

const createTooltipHeader = (titleLines: string[], tableHead: HTMLTableSectionElement): void => {
  titleLines.forEach((title: string) => {
    const tr = document.createElement(`tr`)
    tr.style.borderWidth = `0`

    const th = document.createElement(`th`)
    th.style.borderWidth = `0`

    const text = document.createTextNode(title)

    th.appendChild(text)
    tr.appendChild(th)
    tableHead.appendChild(tr)
  })
}

const getArrowInfoBox = (dataCellsArray: any) => {
  // This is connected to the show_arrow_icon prop in uiconfig that shows if an items value is positive or negative (buy/sell) for the new elplan module. This could be iterated in the future if we want to use the arrows for other purposes than show the arrows as buy/sell
  const iconArrowUp = document.createElement('img')
  const iconArrowDown = document.createElement('img')
  iconArrowUp.src = arrowUp
  iconArrowDown.src = arrowDown

  iconArrowUp.style.marginRight = '5px'
  iconArrowDown.style.marginRight = '5px'
  iconArrowUp.style.maxHeight = '20px'
  iconArrowDown.style.maxHeight = '20px'

  const textArrowDown = document.createTextNode(i18n.t('Sell'))
  const textArrowUp = document.createTextNode(i18n.t('Buy'))

  const tdElementDown = document.createElement('td')
  tdElementDown.style.display = 'inline-flex'
  tdElementDown.style.alignItems = 'center'
  tdElementDown.appendChild(iconArrowUp)
  tdElementDown.appendChild(textArrowUp)

  const tdElementUp = document.createElement('td')
  tdElementUp.style.display = 'inline-flex'
  tdElementUp.style.alignItems = 'center'
  tdElementUp.appendChild(iconArrowDown)
  tdElementUp.appendChild(textArrowDown)

  const separator = document.createElement('hr')
  separator.style.width = '100%'
  separator.style.border = `1px solid ${colors.grey4}`

  const parentTdElement = document.createElement('td')
  parentTdElement.style.display = 'inline-flex'
  parentTdElement.style.gap = '10px'
  parentTdElement.appendChild(tdElementUp)
  parentTdElement.appendChild(tdElementDown)

  dataCellsArray.push(separator)
  dataCellsArray.push(parentTdElement)
}

const createTableRows = (bodyLines: string[], tooltip: any) => {
  const dataCellsArray: any[] = []

  const mappedBodyLinesToChartItems = sortChartItems(
    bodyLines.map((body, i) => {
      //map bodyLines to objects that can be sorted in function
      if (tooltip.dataPoints[i].dataset.order === undefined || tooltip.dataPoints[i].dataset.order === '') {
        return {
          fill: tooltip.dataPoints[i].dataset.fill,
          title: tooltip.dataPoints[i].dataset.label.split(':')[1],
          colors: tooltip.labelColors[i],
          value: tooltip.dataPoints[i].raw,
          showArrowIcon: tooltip.dataPoints[i].dataset.show_arrow_icon,
          borderDash: tooltip.dataPoints[i].dataset.borderDash,
          body: body,
        }
      } else {
        return {
          fill: tooltip.dataPoints[i].dataset.fill,
          title: tooltip.dataPoints[i].dataset.label.split(':')[1],
          order: tooltip.dataPoints[i].dataset.order,
          colors: tooltip.labelColors[i],
          value: tooltip.dataPoints[i].raw,
          showArrowIcon: tooltip.dataPoints[i].dataset.show_arrow_icon,
          borderDash: tooltip.dataPoints[i].dataset.borderDash,
          body: body,
        }
      }
    })
  )
  let tooltipHasItemWithArrowIcon = false

  //create tooltip row items from sorted objects
  mappedBodyLinesToChartItems.forEach((item: any) => {
    const colors = item.colors

    const isFilled = item.fill
    const isDashed = item.borderDash

    const span = document.createElement(`span`)
    span.style.background = isDashed || !isFilled ? null : colors.backgroundColor
    span.style.borderRadius = `50%`
    span.style.borderWidth = `2px`
    span.style.marginRight = `10px`
    span.style.height = `15px`
    span.style.width = `15px`
    span.style.display = `inline-block`
    if (isDashed) {
      span.style.border = `dashed ` + colors.borderColor
    } else if (!isFilled && !isDashed) {
      span.style.border = `2px solid ` + colors.borderColor
    } else {
      span.style.border = colors.borderColor
    }

    const td = document.createElement(`td`)
    td.style.borderWidth = `0`

    const text = document.createTextNode(item.body)
    td.appendChild(span)
    td.appendChild(text)

    if (item.showArrowIcon) {
      tooltipHasItemWithArrowIcon = true
      const icon = document.createElement('img')
      let arrow = arrowUp
      if (typeof item.value === 'number' && item.value < 0) {
        arrow = arrowDown
      }
      icon.src = arrow
      icon.style.marginLeft = '5px'
      icon.style.height = '15px'
      icon.style.width = '15px'
      icon.style.display = 'inline-block'
      td.appendChild(icon)
    }

    if (item.body.length !== 0) {
      //only show tooltip item if there is a value
      dataCellsArray.push(td)
    }
  })

  if (tooltipHasItemWithArrowIcon) {
    getArrowInfoBox(dataCellsArray)
  }

  return dataCellsArray
}

const addDataToRows = (
  dataCellsArray: any[],
  tableBody: HTMLTableSectionElement,
  tableHead: HTMLTableSectionElement,
  tooltipEl: HTMLDivElement
) => {
  // Create each row with multiple datacells if the length is too long
  let maxRows = 10
  if (dataCellsArray.length % 10 < 3) {
    maxRows = 8
  }

  const numberOfColumns = Math.ceil(dataCellsArray.length / maxRows)

  for (let i = 0; i < maxRows; i++) {
    const tr = document.createElement(`tr`)
    tr.style.backgroundColor = `inherit`
    tr.style.borderWidth = `0`

    for (let j = 0; j < numberOfColumns; j++) {
      const dataIndex = i + j * maxRows
      const canAdd: boolean = dataIndex < dataCellsArray.length
      if (canAdd) {
        tr.appendChild(dataCellsArray[dataIndex])
      }
    }
    tableBody.appendChild(tr)
  }
  const tableRoot = tooltipEl.querySelector(`table`)

  if (tableRoot) {
    while (tableRoot.firstChild) {
      tableRoot.firstChild.remove()
    }

    tableRoot.appendChild(tableHead)
    tableRoot.appendChild(tableBody)
  }
}

const positionOfLineChartTooltip = (
  tooltipEl: HTMLDivElement,

  tooltip: any,
  canvasHeight: number,
  chartWidth: number,
  offsetX = 50,
  offsetY?: number
): void => {
  // Display, position, and set styles for font
  tooltipEl.style.opacity = `1`
  tooltipEl.style.top = offsetY !== undefined ? `${offsetY}px` : Math.floor(canvasHeight / 40) + `vh`
  tooltipEl.style.font = tooltip.options.bodyFont.string
  tooltipEl.style.padding = tooltip.options.padding + `px ` + tooltip.options.padding + `px`
  tooltipEl.style.zIndex = `100`

  let left = Math.floor(tooltip.caretX - offsetX - tooltipEl.offsetWidth) + `px`

  if (tooltip.caretX + offsetX + tooltipEl.offsetWidth < chartWidth) {
    left = Math.floor(tooltip.caretX + offsetX) + `px`
  }

  // Ensure tooltip doesn't go off-screen to the left
  const leftValue = parseInt(left, 10)
  if (leftValue < 0) {
    left = `0px`
  }

  tooltipEl.style.left = left
}
const positionOfBarChartTooltip = (
  tooltipEl: HTMLDivElement,

  tooltip: any,
  canvasHeight: number,
  chartWidth: number,
  offsetX = 50,
  offsetY?: number
): void => {
  // Display, position, and set styles for font
  tooltipEl.style.opacity = `1`
  tooltipEl.style.top = offsetY !== undefined ? `${offsetY}px` : Math.floor(canvasHeight / 40) + `vh`
  tooltipEl.style.font = tooltip.options.bodyFont.string
  tooltipEl.style.padding = tooltip.options.padding + `px ` + tooltip.options.padding + `px`
  tooltipEl.style.zIndex = `100`

  if (tooltip.x + offsetX + tooltipEl.offsetWidth < chartWidth) {
    tooltipEl.style.left = Math.floor(tooltip.x + offsetX) + `px`
  } else {
    tooltipEl.style.left = Math.floor(tooltip.x - offsetX - tooltipEl.offsetWidth) + `px`
  }
}

export const externalTooltipHandler = (context: any) => {
  const { chart, tooltip } = context
  const tooltipEl = getOrCreateTooltip(chart)

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = `0`
    return
  }

  const offsetX = tooltip.options?.position_offset_x ?? 50
  const offsetY = tooltip.options?.position_offset_y

  // Set Text
  if (tooltip.body) {
    const titleLines: string[] = tooltip.title || []
    const bodyLines: string[] = tooltip.body.map((b: any) => b.lines)

    const tableHead = document.createElement(`thead`)
    const tableBody = document.createElement(`tbody`)

    if (chart.config.type === `line`) {
      createTooltipHeader(titleLines, tableHead)
    }
    const tableRows: HTMLTableDataCellElement[] = createTableRows(bodyLines, tooltip)
    addDataToRows(tableRows, tableBody, tableHead, tooltipEl)
  }

  const { height: canvasHeight } = chart.canvas
  const { width: chartWidth } = chart.chartArea

  if (chart.config.type === `line`) {
    positionOfLineChartTooltip(tooltipEl, tooltip, canvasHeight, chartWidth, offsetX, offsetY)
  } else if (chart.config.type === `bar`) {
    positionOfBarChartTooltip(tooltipEl, tooltip, canvasHeight, chartWidth, offsetX, offsetY)
  }
}

export const getOrCreateTooltip = (chart: any) => {
  let tooltipEl = chart?.canvas?.parentNode?.querySelector(`div`)

  if (!tooltipEl) {
    tooltipEl = document.createElement(`div`)
    tooltipEl.style.background = `rgba(249, 249, 249, 0.95)`
    tooltipEl.style.borderRadius = `3px`
    tooltipEl.style.color = colors.black
    tooltipEl.style.opacity = 1
    tooltipEl.style.pointerEvents = `none`
    tooltipEl.style.position = `absolute`
    tooltipEl.style.width = `auto`

    const table = document.createElement(`table`)
    table.style.margin = `0px`

    tooltipEl.appendChild(table)
    chart.canvas.parentNode.appendChild(tooltipEl)
  }

  tooltipEl.classList.add(`ChartJSTooltip`)
  return tooltipEl
}

export const tooltipTitle = (tooltipItem: any) => {
  const isoString = Datetime.toISOString(tooltipItem[0].label)
  const nextHour = Datetime.toLocalTime(Datetime.toISOString(moment(tooltipItem[0].label).add(1, `hour`)), `hour`)
  const showTimeAsInterval = tooltipItem.find((item: any) => item.dataset.tooltip_show_time_as_interval)

  // If any item has show time as interval, display an interval for the title of the tooltip
  if (localStorage.getItem('language') === 'en') {
    return Datetime.toLocalTime(isoString, `dayWithDate`)
  }

  if (tooltipItem.length && showTimeAsInterval) {
    return Datetime.toLocalTime(isoString, `longDayText`) + ` - ` + nextHour
  } else if (tooltipItem.length) {
    return Datetime.toLocalTime(isoString, `longDayText`)
  }
  return ``
}

export const tooltipLabel = (
  tooltipItem: TooltipItem<keyof ChartTypeRegistry> & { dataset: ChartJSDatasetConfig }
): string => {
  if (!tooltipItem.dataset.label) {
    return ``
  }

  if (tooltipItem.parsed.y === 0 && tooltipItem.dataset.tooltip_do_not_show_zero_values) {
    return ``
  }

  if (tooltipItem.dataset.tooltip_do_not_show_value) {
    return cleanDatasetLabel(tooltipItem.dataset.label)
  }

  const rawValue =
    tooltipItem.dataset.tooltip_always_positive && tooltipItem.parsed.y != null
      ? Math.abs(tooltipItem.parsed.y)
      : tooltipItem.parsed.y

  const typeObj = getTypeObject(
    rawValue,
    tooltipItem.dataset.data_type,
    tooltipItem.dataset.unit,
    tooltipItem.dataset.decimals
  )

  const label = cleanDatasetLabel(tooltipItem.dataset.label)
  return `${label}: ${typeObj.value} ${typeObj.unit}`
}

export const tooltipLabelBarChart = (
  tooltipItem: TooltipItem<keyof ChartTypeRegistry> & { dataset: ChartJSDatasetConfig }
): string => {
  if (!tooltipItem.dataset.label) {
    return ``
  }

  const rawValue =
    tooltipItem.dataset.tooltip_always_positive && tooltipItem.dataset.data[0] != null
      ? Math.abs(tooltipItem.dataset.data[0])
      : tooltipItem.dataset.data[0]

  const typeObj = getTypeObject(
    rawValue,
    tooltipItem.dataset.data_type,
    tooltipItem.dataset.unit,
    tooltipItem.dataset.decimals
  )

  const label = cleanDatasetLabel(tooltipItem.dataset.label)
  return `${label}: ${typeObj.value} ${typeObj.unit}`
}

function cleanDatasetLabel(label: string): string {
  if (!label.startsWith('INDEX')) {
    return i18n.t(label)
  }

  /*
  [2023-11-27] Mattias:
  ChartJS v4.4.0 has a bug where datasets with the same label are hiding each other, even when they are on different y-axes or stack-groups. Hence, prefixing with the index-key in chart.helper when creating the chartjs dataset. The temporary unique label is then `INDEX${index}:${label}` and we want to clean it to just `${label}` here.
  */

  const removeUpUntilIndex = label.indexOf(':') + 1
  if (removeUpUntilIndex === -1) {
    return i18n.t(label)
  }

  return i18n.t(label.substring(removeUpUntilIndex))
}

export function sortChartItems(items: any): any {
  // The function sorts according to the following:
  // 1. Based on order (0 first).
  // 2. Filled first, then dashed.
  // 3. If the order number is the same, sort by title (filled and dashed separately).
  // 4. If there is no order, sort by title (mixed filled and dashed).
  // 5. If some items has order and other don't, sort by order first.

  const sortedItems: any = items
  sortedItems.sort((a: any, b: any) => {
    const titleA = a?.title ? a.title.toLowerCase() : ''
    const titleB = b?.title ? b.title.toLowerCase() : ''
    const filledA = a?.fill ?? false
    const filledB = b?.fill ?? false

    if (a.order !== undefined && a.order !== '' && b.order !== undefined && b.order !== '') {
      if (a.order === b.order) {
        if (filledA && filledB) {
          return titleA < titleB
        } else if (!filledA && !filledB) {
          if (titleA > titleB) {
            return 1
          }
          if (titleA < titleB) {
            return -1
          }
        }
      }
      if (filledA !== filledB) {
        return filledA ? -1 : 1
      } else if (!filledA !== !filledB) {
        return !filledA ? 1 : -1
      } else {
        return a.order - b.order
      }
    } else if (a.order !== undefined && a.order !== '') {
      return -1
    } else if (a.order !== undefined && b.order !== '') {
      return 1
    } else {
      if (titleA > titleB) {
        return 1
      }
      if (titleA < titleB) {
        return -1
      }
    }
  })

  return sortedItems
}
